import React from "react";
import "./reweaving.css";
import { ConceptER } from "./component/SoundER";
import { VisualER } from "./component/VisualER";
import { LayerModal } from "./component/LayerModal";
import { LazyAutoplayVideo } from "./component/LazyAutoplayVideo";
import thumbnailUrl from "./assets/image/thumbnail.jpg";
import soundengineImageUrl from "./assets/image/soundengine.png";

function useMediaQuery(query: string) {
  const getMatches = () => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.(query).matches ?? false;
  };

  const [matches, setMatches] = React.useState<boolean>(getMatches);

  React.useEffect(() => {
    const mql = window.matchMedia?.(query);
    if (!mql) return;

    const onChange = () => setMatches(mql.matches);
    onChange();

    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

export function ReweavingPage() {
  const SOUND_LAYERS = React.useMemo(
    () =>
      [
        {
          id: "pattern" as const,
          title: "Pattern Analysis Layer",
          description:
            'Extracts rhythmic structures and dynamics from source samples, converting them into a proprietary binary data format.',
        },
        {
          id: "material" as const,
          title: "Material Generation Layer",
          description:
            "Automatically detects micro-acoustic units from long-form recordings. These units are mapped onto 41-TET (41-tone equal temperament), creating 41 independent microtonal sound sources.",
        },
        {
          id: "synthesis" as const,
          title: "Track Synthesis Layer",
          description:
            "Integrates the analyzed patterns with the synthesized microtonal materials to reconstruct a new polyphonic composition.",
        },
      ] as const,
    []
  );

  const VISUAL_LAYERS = React.useMemo(
    () =>
      [
        {
          id: "extraction" as const,
          title: "Extraction Layer",
          description:
            'A "Sequential Analysis" process that fuses WebAudio API data with structural JSON metadata. It synchronizes 41 microtonal phonemes p00~p40 with millisecond precision.',
        },
        {
          id: "mapping" as const,
          title: "Logic Mapping Layer",
          description:
            "Dynamic indicators are mapped to visual variables (flicker, density, spatiality). Using probabilistic triggers, the engine ensures a non-deterministic output; every playback yields a unique visual interpretation.",
        },
        {
          id: "rendering" as const,
          title: "Rendering Layer",
          description:
            "A 2D Canvas pipeline that treats raw digital strings—representing the internal state of the SoundEngine—as texture. It visualizes the density of information itself, merging data-driven aesthetics with sonic energy.",
        },
      ] as const,
    []
  );

  type SoundLayerId = (typeof SOUND_LAYERS)[number]["id"];
  type VisualLayerId = (typeof VISUAL_LAYERS)[number]["id"];

  const [activeErCard, setActiveErCard] = React.useState<SoundLayerId | null>(null);
  const soundEngineRef = React.useRef<HTMLDivElement | null>(null);
  const [linkPath, setLinkPath] = React.useState<string | null>(null);

  const [activeVisualErCard, setActiveVisualErCard] = React.useState<VisualLayerId | null>(
    null
  );
  const visualEngineRef = React.useRef<HTMLDivElement | null>(null);
  const [visualLinkPath, setVisualLinkPath] = React.useState<string | null>(null);

  const isMobile = useMediaQuery("(max-width: 768px)");

  const [layerModal, setLayerModal] = React.useState<
    | null
    | { kind: "sound"; id: SoundLayerId }
    | { kind: "visual"; id: VisualLayerId }
  >(null);

  const closeLayerModal = React.useCallback(() => {
    setLayerModal((prev) => {
      if (prev?.kind === "sound") setActiveErCard(null);
      if (prev?.kind === "visual") setActiveVisualErCard(null);
      return null;
    });
  }, []);

  const recomputeLink = React.useCallback(() => {
    if (!activeErCard) {
      setLinkPath(null);
      return;
    }
    const container = soundEngineRef.current;
    if (!container) return;

    const startEl = document.getElementById(`layer-${activeErCard}`);
    const endEl = document.getElementById(`card-${activeErCard}`);
    if (!startEl || !endEl) {
      setLinkPath(null);
      return;
    }

    const c = container.getBoundingClientRect();
    const s = startEl.getBoundingClientRect();
    const e = endEl.getBoundingClientRect();

    const x1 = s.left + s.width / 2 - c.left;
    const y1 = s.top - c.top;
    const x2 = e.left + e.width / 2 - c.left;
    const y2 = e.bottom - c.top;
    const yMid = (y1 + y2) / 2;

    setLinkPath(`M ${x1} ${y1} Q ${x1} ${yMid} ${x2} ${y2}`);
  }, [activeErCard]);

  React.useLayoutEffect(() => {
    recomputeLink();
  }, [recomputeLink]);

  React.useEffect(() => {
    if (!activeErCard) return;
    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(recomputeLink);
    };

    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, { passive: true });

    const erWrapper = soundEngineRef.current?.querySelector(".er-diagram-wrapper");
    erWrapper?.addEventListener("scroll", schedule, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule);
      erWrapper?.removeEventListener("scroll", schedule);
    };
  }, [activeErCard, recomputeLink]);

  const recomputeVisualLink = React.useCallback(() => {
    if (!activeVisualErCard) {
      setVisualLinkPath(null);
      return;
    }
    const container = visualEngineRef.current;
    if (!container) return;

    const startEl = document.getElementById(`v-layer-${activeVisualErCard}`);
    const endEl = document.getElementById(`v-card-${activeVisualErCard}`);
    if (!startEl || !endEl) {
      setVisualLinkPath(null);
      return;
    }

    const c = container.getBoundingClientRect();
    const s = startEl.getBoundingClientRect();
    const e = endEl.getBoundingClientRect();

    const x1 = s.left + s.width / 2 - c.left;
    const y1 = s.top + s.height / 2 - c.top;
    const x2 = e.left + e.width / 2 - c.left;
    const y2 = e.top + e.height / 2 - c.top;

    const dx = Math.abs(x2 - x1);
    const dy = Math.abs(y2 - y1);

    if (dx > dy) {
      const xMid = (x1 + x2) / 2;
      setVisualLinkPath(`M ${x1} ${y1} Q ${xMid} ${y1} ${x2} ${y2}`);
      return;
    }

    const yMid = (y1 + y2) / 2;
    setVisualLinkPath(`M ${x1} ${y1} Q ${x1} ${yMid} ${x2} ${y2}`);
  }, [activeVisualErCard]);

  React.useLayoutEffect(() => {
    recomputeVisualLink();
  }, [recomputeVisualLink]);

  React.useEffect(() => {
    if (!activeVisualErCard) return;
    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(recomputeVisualLink);
    };

    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, { passive: true });

    const erWrapper = visualEngineRef.current?.querySelector(".er-diagram-wrapper");
    erWrapper?.addEventListener("scroll", schedule, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule);
      erWrapper?.removeEventListener("scroll", schedule);
    };
  }, [activeVisualErCard, recomputeVisualLink]);

  const soundModalData = React.useMemo(() => {
    if (!layerModal || layerModal.kind !== "sound") return null;
    return SOUND_LAYERS.find((l) => l.id === layerModal.id) ?? null;
  }, [layerModal, SOUND_LAYERS]);

  const visualModalData = React.useMemo(() => {
    if (!layerModal || layerModal.kind !== "visual") return null;
    return VISUAL_LAYERS.find((l) => l.id === layerModal.id) ?? null;
  }, [layerModal, VISUAL_LAYERS]);

  return (
    <div className="app-container reweaving-page">
      <main className="main-content reweaving-main-content">
        <h1 className="reweaving-artwork-title">REWEAVING</h1>
        <div className="reweaving-hero">
          <img
            className="reweaving-hero-image"
            src={thumbnailUrl}
            alt="REWEAVING thumbnail"
            loading="eager"
            decoding="async"
          />
        </div>
        <section id="concept" className="section">
          <h2 className="section-title">1. Concept</h2>
          <div className="content-block">
            <p className="lead-text">
              REWEAVING is an algorithmic deconstruction and synthesis project that treats
              musical works as "pure data patterns." Inspired by the multi-layered
              architecture of the artist Djrum, this project re-engineers the relationship
              between "original" and "sampling."
            </p>
            <p>
              By dismantling the temporal axis and textures of existing audio, the system
              re-weaves them into a new mathematical framework, 41-Equal Division of the
              Octave (41-EDO). The goal is to preserve the "soul" of the source material
              while manifesting a radical temporal experience through custom-built sound and
              visual engines.
            </p>
          </div>
        </section>

        <section id="technology" className="section">
          <h2 className="section-title">2. Technology</h2>

          <div className="subsection soundengine-subsection" ref={soundEngineRef}>
            <h3 className="subsection-title">2-1. SoundEngine</h3>
            <p className="tech-stack">Tech stack: Rust</p>
            <p className="tech-stack-note">Custom-built for high-precision micro-temporal synthesis.</p>
            <div className="tech-media-wrap" aria-label="SoundEngine diagram">
              <img
                className="tech-media"
                src={soundengineImageUrl}
                alt="SoundEngine diagram"
                loading="lazy"
                decoding="async"
              />
            </div>

            <svg className="er-link-overlay" aria-hidden="true">
              <defs>
                <marker
                  id="er-link-arrow"
                  markerWidth="10"
                  markerHeight="10"
                  refX="8"
                  refY="5"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <path d="M 0 0 L 10 5 L 0 10 z" className="er-link-arrowhead" />
                </marker>
              </defs>
              {linkPath ? (
                <path
                  d={linkPath}
                  className="er-link-line"
                  markerEnd="url(#er-link-arrow)"
                />
              ) : null}
            </svg>

            <div className="er-diagram-wrapper">
              <h4 className="subsection-subtitle">Data Architecture</h4>
              <ConceptER
                activeCard={activeErCard}
                onCardSelect={
                  isMobile
                    ? (id) => {
                        setActiveErCard(id);
                        setLayerModal({ kind: "sound", id });
                      }
                    : undefined
                }
              />
            </div>

            <div className="tech-grid">
              {SOUND_LAYERS.map((layer) => (
                <div
                  key={layer.id}
                  className="tech-card"
                  id={`layer-${layer.id}`}
                  onMouseEnter={() => setActiveErCard(layer.id)}
                  onMouseLeave={() => setActiveErCard(null)}
                >
                  <h4>{layer.title}</h4>
                  <p>{layer.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="subsection visualengine-subsection" ref={visualEngineRef}>
            <h3 className="subsection-title">2-2. VisualEngine</h3>
            <p className="tech-stack">Tech stack: TypeScript</p>
            <p className="tech-stack-note">
              A real-time generative pipeline synchronizing data density with visual dynamism.
            </p>
            <div className="tech-media-wrap" aria-label="VisualEngine demo video">
              <LazyAutoplayVideo className="tech-media" ariaLabel="VisualEngine demo video" />
            </div>

            <svg className="er-link-overlay" aria-hidden="true">
              <defs>
                <marker
                  id="visual-er-link-arrow"
                  markerWidth="10"
                  markerHeight="10"
                  refX="8"
                  refY="5"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <path d="M 0 0 L 10 5 L 0 10 z" className="er-link-arrowhead" />
                </marker>
              </defs>
              {visualLinkPath ? (
                <path
                  d={visualLinkPath}
                  className="er-link-line"
                  markerEnd="url(#visual-er-link-arrow)"
                />
              ) : null}
            </svg>

            <div className="visualengine-layout">
              <div className="er-diagram-wrapper visualengine-diagram-wrapper">
                <h4 className="subsection-subtitle">Data Architecture</h4>
                <VisualER
                  activeCard={activeVisualErCard}
                  onCardSelect={
                    isMobile
                      ? (id) => {
                          setActiveVisualErCard(id);
                          setLayerModal({ kind: "visual", id });
                        }
                      : undefined
                  }
                />
              </div>

              <div className="visualengine-text-cards" aria-label="Layer descriptions">
                {VISUAL_LAYERS.map((layer) => (
                  <div
                    key={layer.id}
                    className="tech-card"
                    id={`v-layer-${layer.id}`}
                    onMouseEnter={() => setActiveVisualErCard(layer.id)}
                    onMouseLeave={() => setActiveVisualErCard(null)}
                  >
                    <h4>{layer.title}</h4>
                    <p>{layer.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <LayerModal
        open={!!layerModal}
        subtitle={
          layerModal?.kind === "sound"
            ? "SoundEngine"
            : layerModal?.kind === "visual"
              ? "VisualEngine"
              : undefined
        }
        title={soundModalData?.title ?? visualModalData?.title ?? ""}
        onClose={closeLayerModal}
      >
        <p className="layer-modal-text">{soundModalData?.description ?? visualModalData?.description ?? ""}</p>
      </LayerModal>
    </div>
  );
}

export default ReweavingPage;
